import { AxiosStatic } from 'axios';
import { ICurrency } from '@/common/interfaces/index.interface';
import {
  IIncentive,
  IIncentiveRanking,
  IIncentivesData,
  ISearchResults,
  IVotePartner,
} from '@/common/interfaces/single-incentive.interface';
import { IUpdateAddress } from '@/common/interfaces/my-profile.interface';
import {
  IApiAddress,
  IApiPartnerInfo,
  IApiRequestObject,
  IApiSearchResult,
} from '@/common/interfaces/api.interface';
import {
  IPartnerApplication,
  IPartnerApplicationResponse,
} from '@/common/interfaces/partner-application.interface';
import { IAddress } from '@/common/interfaces/shared.interface';
import { IPartnerSearchResult } from '@/common/interfaces/partners.interface';
import ApiHelper from '../helpers/ApiHelper';
import ApiRoutes from '../api/ApiRoutes';
import { TApiResponse } from '../common/types/api.types';
import { IApiBarometerData } from '../common/interfaces/barometer.interface';
import { IDashboardData } from '../common/interfaces/dashboard.interface';
import { ICalendarData } from '../common/interfaces/calendar.interface';
import { ICountry, ISalutation } from '../common/interfaces/config.interface';
import { IProduct } from '../common/interfaces/products.interface';
import { IShoppingCart } from '../common/interfaces/cart.interface';
import config from '../../config';
import { IImage } from '@/common/interfaces/image.interface';
import { ICustomerSession } from '@/common/interfaces/customers.interface';

export default class ApiService {
  private axios: AxiosStatic;

  constructor(axios: AxiosStatic) {
    this.axios = axios;
    this.axios.defaults.withCredentials = true;
  }

  private async fetch<T>(apiRoute: ApiRoutes, object: IApiRequestObject = {}): Promise<TApiResponse<T>> {
    let data;
    try {
      const requestConfig = ApiHelper.getRequestConfig(apiRoute, object);
      requestConfig.baseURL = config.api.baseurl;
      ({ data } = await this.axios.request(requestConfig));
    } catch (err) {
      ApiHelper.handleKnownApiError(err, apiRoute);
    }
    return ApiHelper.cleanResponseData<T>(data);
  }

  public async postBarometerData(): Promise<IApiBarometerData> {
    const response = await this.fetch<IApiBarometerData>(ApiRoutes.postBarometerData);
    return response.data;
  }

  public async getCurrencies(): Promise<ICurrency[]> {
    const response = await this.fetch<ICurrency[]>(ApiRoutes.getCurrencies);
    return response.data ?? [];
  }

  public async getDashboardData(): Promise<IDashboardData> {
    const response = await this.fetch<IDashboardData>(ApiRoutes.getDashboardData);
    return response.data;
  }

  public async getLoginCheck(): Promise<boolean> {
    const response = await this.fetch<undefined>(ApiRoutes.getLoginCheck);
    return response.success ?? false;
  }

  public async getCalendarData(options: IApiRequestObject): Promise<ICalendarData> {
    const response = await this.fetch<ICalendarData>(ApiRoutes.getCalendarData, options);
    return response.data;
  }

  public async getPartnerInfo(options: IApiRequestObject): Promise<TApiResponse<IApiPartnerInfo>> {
    const response = await this.fetch<IApiPartnerInfo>(ApiRoutes.getPartnerInfo, options);
    return {
      data: response.data,
      success: response.success ?? false,
    };
  }

  public async getProducts(options: IApiRequestObject): Promise<IProduct[]> {
    const response = await this.fetch<IProduct[]>(ApiRoutes.getProducts, options);
    return response.data;
  }

  public async getProductInfos(options: IApiRequestObject): Promise<IProduct> {
    const response = await this.fetch<IProduct>(ApiRoutes.getProductByMatchcode, options);
    return response.data;
  }

  public async getReducedProducts(options: IApiRequestObject): Promise<IProduct[]> {
    const response = await this.fetch<IProduct[]>(ApiRoutes.getReducedProducts, options);
    return response.data;
  }

  public async getSalutations(): Promise<ISalutation[]> {
    const response = await this.fetch<ISalutation[]>(ApiRoutes.getSalutations);
    return response.data;
  }

  public async removeNewAddress(customerId: string, addressId: number): Promise<boolean> {
    const response = await this.fetch(ApiRoutes.removeNewAddress, {
      params: {
        addressId,
        customerId,
      },
    });

    return response.success ?? false;
  }

  public async patchNewAddress(addressData: IAddress | IUpdateAddress): Promise<IAddress> {
    const response = await this.fetch<IApiAddress>(ApiRoutes.patchNewAddress, { data: { ...addressData } });

    return {
      city: response.data.city,
      country: response.data.country,
      countryISO: response.data.countryISO,
      countryId: response.data.countryId,
      deliveryDefault: response.data.deliveryDefault,
      extra: response.data.extra,
      id: response.data.id,
      receiptDefault: response.data.receiptDefault,
      recipient: response.data.recipient,
      street: response.data.street,
      streetNumber: response.data.streetNumber,
      zipCode: response.data.zipCode,
    };
  }

  public async updateNewsletter(options: IApiRequestObject): Promise<boolean> {
    const response = await this.fetch<undefined>(ApiRoutes.updateNewsletter, options);
    return response.success ?? false;
  }

  public async unsubscribeNewsletter(options: IApiRequestObject): Promise<boolean> {
    const response = await this.fetch<undefined>(ApiRoutes.unsubscribeNewsletter, options);
    return response.success ?? false;
  }

  public async postNewsletterConfirm(options: IApiRequestObject): Promise<void> {
    await this.fetch<void>(ApiRoutes.postNewsletterConfirm, options);
  }

  public async postEmailForChallengeRegistration(email: string): Promise<boolean> {
    const response = await this.fetch<void>(ApiRoutes.postChallengeRegistrationEmail, {
      data: {
        email,
      },
    });
    return response.success ?? false;
  }

  public async getSearchData(options: IApiRequestObject): Promise<TApiResponse<IApiSearchResult[]>> {
    return this.fetch<IApiSearchResult[]>(ApiRoutes.getSearchData, options);
  }

  public async addItemToCart(options: IApiRequestObject): Promise<IShoppingCart> {
    const response = await this.fetch<IShoppingCart>(ApiRoutes.postItemToCart, options);
    return response.data;
  }

  public async updateCartItem(options: IApiRequestObject): Promise<IShoppingCart> {
    const response = await this.fetch<IShoppingCart>(ApiRoutes.updateCartItem, options);
    return response.data;
  }

  public async removeItemFromCart(options: IApiRequestObject): Promise<IShoppingCart> {
    const response = await this.fetch<IShoppingCart>(ApiRoutes.deleteItemFromCart, options);
    return response.data;
  }

  public async getPartnerApplication(options: IApiRequestObject): Promise<IPartnerApplication> {
    const response = await this.fetch<IPartnerApplication>(ApiRoutes.getPartnerApplication, options);
    return response.data;
  }

  public async getAddresses(options: IApiRequestObject): Promise<IAddress[]> {
    const response = await this.fetch<IAddress[]>(ApiRoutes.getAddresses, options);
    return response.data;
  }

  public async getAddressById(options: IApiRequestObject): Promise<IAddress> {
    const response = await this.fetch<IAddress[]>(ApiRoutes.getAddressById, options);
    return response.data[0];
  }

  public async getCountries(showAllCountries = false, options: IApiRequestObject = {}): Promise<ICountry[]> {
    const response = await this.fetch<ICountry[]>(showAllCountries ? ApiRoutes.getAllCountries : ApiRoutes.getCountries, options);
    return response.data;
  }

  public async postPartnerSearch(options: IApiRequestObject): Promise<IPartnerSearchResult[]> {
    const response = await this.fetch<IPartnerSearchResult[]>(ApiRoutes.postPartnerSearch, options);
    return response.data;
  }

  public async postPartnerApplication(options: IApiRequestObject): Promise<IPartnerApplicationResponse> {
    const response = await this.fetch<IPartnerApplicationResponse>(ApiRoutes.postPartnerApplication, options);
    return response.data;
  }

  public async postPartnerApplicationPreCheck(options: IApiRequestObject): Promise<boolean> {
    const response = await this.fetch<void>(ApiRoutes.postPartnerApplicationPreCheck, options);
    return response.success ?? false;
  }

  public async getPartnerApplicationProducts(options: IApiRequestObject = {}): Promise<IProduct[]> {
    const response = await this.fetch<IProduct[]>(ApiRoutes.getPartnerApplicationProducts, options);
    return response.data;
  }

  public async postPartnerImage(options: IApiRequestObject): Promise<string> {
    const response = await this.fetch<string>(ApiRoutes.postPartnerImage, options);
    return response.data;
  }

  public async getCarts(options: IApiRequestObject = {}): Promise<IShoppingCart> {
    const response = await this.fetch<IShoppingCart>(ApiRoutes.getCart, options);
    return response.data;
  }

  public async postCartCreate(options: IApiRequestObject = {}): Promise<IShoppingCart> {
    const response = await this.fetch<IShoppingCart>(ApiRoutes.postCartCreate, options);
    return response.data;
  }

  public async deleteCart(options: IApiRequestObject): Promise<boolean> {
    const response = await this.fetch<void>(ApiRoutes.deleteCart, options);
    return response.success || false;
  }

  public async postItemToCart(options: IApiRequestObject): Promise<TApiResponse<IShoppingCart>> {
    return this.fetch<IShoppingCart>(ApiRoutes.postItemToCart, options);
  }

  public async getCartCount(): Promise<number> {
    let amount;
    try {
      const response = await this.fetch<{ amount: number }>(ApiRoutes.getCartCount, {});
      amount = response.amount ?? 0;
    } catch (err) {
      amount = 0;
    }
    return amount;
  }

  public async verifyTaxNumber(options: IApiRequestObject): Promise<boolean> {
    const response = await this.fetch<void>(ApiRoutes.verifyTaxNumber, options);
    return response.success || false;
  }

  public async getIncentives(): Promise<IIncentive[]> {
    const response = await this.fetch<IIncentive[]>(ApiRoutes.getIncentives, {});
    return response.data;
  }

  public async getIncentiveRanking(options: IApiRequestObject): Promise<IIncentiveRanking> {
    const response = await this.fetch<IIncentiveRanking>(ApiRoutes.getIncentiveRanking, options);
    return {
      ranking: response.data.ranking,
      rankingCount: response.data.rankingCount as number,
    };
  }

  public async getIncentiveClimberRanking(options: IApiRequestObject): Promise<IIncentiveRanking> {
    const response = await this.fetch<IIncentiveRanking>(ApiRoutes.getIncentiveRanking, options);
    return {
      climber: response.data.climber,
      climberCount: response.data.climberCount as number,
    };
  }

  public async getIncentiveData(options: IApiRequestObject): Promise<IIncentivesData> {
    const response = await this.fetch<IIncentivesData>(ApiRoutes.getIncentiveData, options);
    return response.data;
  }

  public async getSearchResults(options: IApiRequestObject): Promise<ISearchResults[]> {
    const response = await this.fetch<ISearchResults[]>(ApiRoutes.getSearchResults, options);
    return response.data;
  }

  public async votePartner(options: IApiRequestObject): Promise<IVotePartner> {
    const response = await this.fetch<IVotePartner>(ApiRoutes.votePartner, options);
    return response.data;
  }

  public async redeemCoupon(options: IApiRequestObject): Promise<IShoppingCart> {
    const response = await this.fetch<IShoppingCart>(ApiRoutes.redeemCoupon, options);
    return response.data;
  }

  public async removeCoupon(options: IApiRequestObject): Promise<IShoppingCart> {
    const response = await this.fetch<IShoppingCart>(ApiRoutes.removeCoupon, options);
    return response.data;
  }

  public async getDocuSignRecipientViewUrl(options: IApiRequestObject): Promise<string> {
    const response = await this.fetch<{ url: string }>(ApiRoutes.createDocuSignRecipientView, options);
    return response.data.url;
  }

  public async postItalianPartnerPicture(options: IApiRequestObject): Promise<IImage> {
    const response = await this.fetch<IImage>(ApiRoutes.postItalianPartnerPicture, options);
    return response.data;
  }

  public async updateItalianPartnerPicture(options: IApiRequestObject): Promise<IImage> {
    const response = await this.fetch<IImage>(ApiRoutes.updateItalianPartnerPicture, options);
    return response.data;
  }

  public async getItalianPartnerPicture(): Promise<IImage> {
    const response = await this.fetch<IImage>(ApiRoutes.getItalianPartnerPicture, {});
    return response.data;
  }

  public async postImage(image: File): Promise<IImage> {
    const imageFormData = new FormData();
    imageFormData.append(`file`, image);

    const response = await this.fetch<IImage>(ApiRoutes.postImage, {
      data: imageFormData,
      formData: true,
    });
    return response.data;
  }

  public async deleteImage(key: string): Promise<void> {
    await this.fetch<void>(ApiRoutes.deleteImage, {
      params: {
        key,
      },
    });
  }

  public async getCustomerSession(): Promise<ICustomerSession> {
    const response = await this.fetch<ICustomerSession>(ApiRoutes.customerSession);
    return response.data;
  }
}
